import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../../../context/CartContext';
import { useAuth } from '../../../context/AuthContext';
import { commonShadow } from '../../../utils/uiEffects';

export default function Navigation() {
  const { cartCount, setIsCartOpen } = useCart();
  const { isAuthenticated, openAuthModal } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const [activeLink, setActiveLink] = useState('menu');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Update active link based on URL
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/menu')) {
      setActiveLink('menu');
    } else if (path.includes('/merchandise')) {
      setActiveLink('merchandise');
    } else if (path.includes('/gift-cards')) {
      setActiveLink('gifting');
    } else if (path.includes('/contact')) {
      setActiveLink('contact');
    } else if (path.includes('/account')) {
      setActiveLink('account');
    } else if (path === '/' || path === __BASE_PATH__) {
      setActiveLink('home');
    } else {
      setActiveLink(''); // No underline for profile, auth, etc.
    }
  }, [location]);

  const navLinks = [
    {
      id: 'home',
      label: 'Home',
      action: () => {
        if (window.location.pathname === '/' || window.location.pathname === __BASE_PATH__) {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          window.REACT_APP_NAVIGATE('/');
        }
      }
    },
    {
      id: 'menu',
      label: 'Menu',
      action: () => {
        const storedStore = localStorage.getItem('tcr_selected_store');
        let storeId = null;
        if (storedStore) {
          try {
            const parsed = JSON.parse(storedStore);
            storeId = parsed.id || parsed._id;
          } catch {
            storeId = null;
          }
        }
        // Navigate to /menu - the menu page will auto-select nearest store if no storeId
        window.REACT_APP_NAVIGATE(storeId ? `/menu/${storeId}` : '/menu');
      }
    },
    { id: 'merchandise', label: 'Merchandise', action: () => window.REACT_APP_NAVIGATE('/merchandise') },
    { id: 'gifting', label: 'Gifting', action: () => window.REACT_APP_NAVIGATE('/gift-cards') },
    { id: 'contact', label: 'Contact Us', action: () => window.REACT_APP_NAVIGATE('/contact') }
  ];

  const handleProfileClick = () => {
    if (isAuthenticated) {
      // profile page is served at /account
      window.REACT_APP_NAVIGATE('/account');
    } else {
      openAuthModal();
    }
  };

  const mobileBottomLinks = [
    {
      id: 'home',
      label: 'Home',
      icon: 'ri-home-5-line',
      action: () => window.REACT_APP_NAVIGATE('/')
    },
    {
      id: 'menu',
      label: 'Menu',
      icon: 'ri-restaurant-line',
      action: () => {
        const storedStore = localStorage.getItem('tcr_selected_store');
        let storeId = null;
        if (storedStore) {
          try {
            const parsed = JSON.parse(storedStore);
            storeId = parsed.id || parsed._id;
          } catch {
            storeId = null;
          }
        }
        window.REACT_APP_NAVIGATE(storeId ? `/menu/${storeId}` : '/menu');
      }
    },
    {
      id: 'merchandise',
      label: 'Merch',
      icon: 'ri-t-shirt-line',
      action: () => window.REACT_APP_NAVIGATE('/merchandise')
    },
    {
      id: 'store',
      label: 'Store',
      icon: 'ri-map-pin-line',
      action: () => window.REACT_APP_NAVIGATE('/find-store')
    }
  ];

  const handleLinkClick = (link) => {
    const isHomePage = window.location.pathname === '/' || window.location.pathname === __BASE_PATH__;

    if (link.action) {
      link.action();
      setActiveLink(link.id);
    } else if (link.href) {
      if (isHomePage) {
        const element = document.querySelector(link.href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
        setActiveLink(link.id);
      } else {
        window.REACT_APP_NAVIGATE(`/${link.href}`);
        setActiveLink(link.id);
      }
    }
  };

  return (
    <>
    <motion.header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'glass-panel-strong border-b border-[#C5A021]/20' : 'bg-transparent'
        }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="page-container py-2 md:py-3 flex items-center justify-between">
        <motion.div
          className="flex items-center gap-3 cursor-pointer"
          onClick={() => window.REACT_APP_NAVIGATE && window.REACT_APP_NAVIGATE('/')}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <div className="w-11 h-11 md:w-14 md:h-14 flex items-center justify-center overflow-hidden">
            <img src="/logo.png" alt="Logo" className="w-full h-full object-contain" onError={(e) => {
              e.target.style.display = 'none';
              const icon = document.createElement('i');
              icon.className = 'ri-cake-3-fill text-2xl md:text-3xl text-[#C5A021]';
              e.target.parentElement?.appendChild(icon);
            }} />
          </div>
          <div className="hidden sm:block">
            <h1 className={`font-syncopate text-lg md:text-xl font-bold transition-colors duration-300 ${scrolled ? 'text-[#140B07]' : 'text-[#F8F4F0]'}`}></h1>
            <p className={`font-outfit text-[10px] md:text-xs transition-colors duration-300 ${scrolled ? 'text-[#140B07]/70' : 'text-[#F8F4F0]/60'}`}></p>
          </div>
          <div className="sm:hidden">
            <h1 className={`font-syncopate text-lg font-bold transition-colors duration-300 ${scrolled ? 'text-[#140B07]' : 'text-[#F8F4F0]'}`}></h1>
          </div>
        </motion.div>

        <div className="hidden md:flex items-center gap-8 lg:gap-12">
          {navLinks.map((link) => (
            <motion.button
              key={link.id}
              onClick={() => handleLinkClick(link)}
              className={`relative text-sm tracking-wider cursor-pointer whitespace-nowrap transition-all duration-300 font-bold ${scrolled ? 'text-[#C5A021]' : 'text-[#F8F4F0]'} ${activeLink === link.id ? 'text-shadow-gold' : ''}`}
              whileHover={{ y: -2 }}
            >
              {link.label}
              {activeLink === link.id && (
                <motion.div
                  className={`absolute -bottom-1 left-0 right-0 h-0.5 shadow-[0_0_10px_#C5A021] bg-[#C5A021]`}
                  layoutId="underline"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 0.3 }}
                />
              )}
            </motion.button>
          ))}
        </div>

        <div className="flex items-center gap-3 md:gap-6">
          <motion.button
            onClick={handleProfileClick}
            className={`w-10 h-10 md:w-10 md:h-10 rounded-full bg-[#25160F]/50 border border-[#C5A021]/20 flex items-center justify-center cursor-pointer ${commonShadow}`}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.95 }}
          >
            <i className="ri-user-line text-[#C5A021] text-base md:text-lg"></i>
          </motion.button>

          <motion.button
            onClick={() => window.REACT_APP_NAVIGATE && window.REACT_APP_NAVIGATE('/checkout')}
            className={`relative w-10 h-10 md:w-10 md:h-10 rounded-full bg-[#25160F]/50 border border-[#C5A021]/20 flex items-center justify-center cursor-pointer ${commonShadow}`}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.95 }}
          >
            <i className="ri-shopping-cart-line text-[#C5A021] text-base md:text-lg"></i>
            {cartCount > 0 && (
              <motion.span
                className="absolute -top-1 -right-1 w-4 h-4 md:w-5 md:h-5 bg-[#C5A021] rounded-full flex items-center justify-center text-[8px] md:text-[10px] font-semibold text-[#140B07]"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 500 }}
              >
                {cartCount}
              </motion.span>
            )}
          </motion.button>

          {/* Mobile Menu Toggle */}
          <motion.button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="hidden md:hidden w-10 h-10 rounded-full bg-[#25160F]/50 border border-[#C5A021]/20 items-center justify-center cursor-pointer"
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.95 }}
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-4-line'} text-[#C5A021] text-lg`}></i>
          </motion.button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden glass-panel-strong border-b border-[#C5A021]/20 overflow-hidden"
          >
            <div className="flex flex-col p-6 gap-4">
              {navLinks.map((link) => (
                <motion.button
                  key={link.id}
                  onClick={() => {
                    handleLinkClick(link);
                    setIsMobileMenuOpen(false);
                  }}
                  className={`text-left text-lg tracking-wider font-outfit font-bold transition-all ${activeLink === link.id ? 'text-[#C5A021] text-shadow-gold' : 'text-[#F8F4F0]'
                    }`}
                  whileTap={{ x: 10 }}
                >
                  {link.label}
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

    </motion.header>

    {/* Mobile Bottom Navigation (app-like) */}
    <div className="md:hidden fixed bottom-0 left-0 right-0 z-[90] px-3 pb-3 pt-2 bg-gradient-to-t from-[#0A0604] via-[#0A0604]/95 to-transparent">
      <div className="mx-auto max-w-md rounded-2xl border border-[#C5A021]/20 bg-[#140B07]/95 backdrop-blur-md px-2 py-2 shadow-[0_-8px_30px_rgba(0,0,0,0.45)]">
          <div className="grid grid-cols-4 gap-1">
            {mobileBottomLinks.map((link) => {
              const isActive = activeLink === link.id;
              return (
                <button
                  key={link.id}
                  type="button"
                  onClick={link.action}
                  className={`relative flex flex-col items-center justify-center py-2 rounded-xl transition-all ${isActive ? 'bg-[#C5A021]/20 text-[#C5A021]' : 'text-[#F8F4F0]/70'}`}
                >
                  <i className={`${link.icon} text-lg`} />
                  <span className="text-[10px] font-outfit mt-0.5">{link.label}</span>
                </button>
              );
            })}
          </div>
      </div>
    </div>
    </>
  );
}
